import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiCreditCard,
  FiDatabase,
  FiEdit3,
  FiExternalLink,
  FiFileText,
  FiGlobe,
  FiInfo,
  FiLink,
  FiLock,
  FiMail,
  FiMapPin,
  FiPercent,
  FiRefreshCw,
  FiRotateCcw,
  FiShield,
  FiTarget,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";
import MarketingPageLayout from "../../components/marketing/MarketingPageLayout";
import "../../styles/auth.css";

const TERMS = [
  {
    id: 1,
    title: "Introduction",
    icon: FiFileText,
    paragraphs: [
      "Software Solutions Development India (SSDI) is the developer and service provider of the FastRecovery App/Web platform.",
      "FastRecovery has been developed to securely and efficiently deliver finance-related vehicle information uploaded by banks, Non-Banking Financial Companies (NBFCs), and authorized legal repossession agencies to registered and authorized users.",
      "SSDI acts as a technology service provider that facilitates information sharing and coordination between banks, NBFCs, authorized recovery agencies, and their permitted users.",
      "Users must comply with these Terms and Conditions, applicable laws, and the permissions granted to them while using FastRecovery.",
      "FastRecovery's operations are subject to applicable Indian laws, including the Information Technology Act, 2000, the Digital Personal Data Protection Act, 2023, and other applicable rules and regulations, to the extent in force and applicable.",
      "By registering for, accessing, or using FastRecovery, you agree to comply with these Terms and Conditions.",
    ],
  },
  {
    id: 2,
    title: "Data and Location",
    icon: FiMapPin,
    paragraphs: [
      "FastRecovery provides access to vehicle-related information uploaded by participating banks, NBFCs, and authorized legal recovery agencies.",
      "Access to information is determined by the user's role, permissions, company association, applicable agreements, and the relevant case requirements.",
      "Location information may be collected or recorded through tracking features when enabled and lawfully authorized for a relevant case. Such information may be made available to authorized bank administrators, bank staff, or legal recovery agency administrators and staff according to their assigned permissions and applicable agreements.",
      "SSDI's own administrative panel is designed not to provide access to individual vehicle-tracing locations or tracking-related information. Access to such information is restricted according to the platform's access controls.",
      "Users must use location information only for authorized and lawful purposes. Unauthorized tracking, disclosure, or misuse of location information is prohibited.",
    ],
  },
  {
    id: 3,
    title: "Responsibility for Data and Transactions",
    icon: FiShield,
    paragraphs: [
      "The bank, NBFC, or authorized legal recovery agency uploading information is responsible for the accuracy, authenticity, legality, and authorization of the data it provides.",
      "SSDI provides the technological infrastructure for accessing and transferring such information and is not the original source of the uploaded vehicle or case data.",
      "Each participating organization is responsible for ensuring that it has the necessary rights, permissions, authorizations, and lawful basis to upload and share information through FastRecovery.",
      "Users are responsible for verifying the information relevant to their assigned cases and complying with applicable legal requirements.",
      "FastRecovery provides search, tracing, coordination, and related features according to the services and packages made available to the user.",
    ],
  },
  {
    id: 4,
    title: "Agency and User Rules",
    icon: FiAlertTriangle,
    paragraphs: [
      "All agencies and users must comply with applicable laws, lawful recovery procedures, relevant RBI guidelines where applicable, and the authorization granted by the concerned bank or NBFC.",
      "Users and agencies must not:",
    ],
    list: [
      "Threaten, abuse, intimidate, harass, or unlawfully use force against any person.",
      "Engage in unlawful vehicle seizure, repossession, or recovery activities.",
      "Misuse, sell, disclose, or distribute vehicle or personal information without proper authorization.",
      "Access cases, records, or tracking information beyond their permitted role or authorization.",
      "Use FastRecovery to commit fraud, impersonation, harassment, or any other unlawful activity.",
    ],
    afterList: [
      "Agencies must maintain valid authorization from the relevant bank or NBFC for the recovery activities they undertake.",
      "Where SSDI reasonably identifies suspected misuse, unauthorized access, unlawful activity, or a serious violation of these Terms, it may restrict, suspend, or terminate the relevant account, subject to applicable law and any required procedures.",
      "Refund eligibility, where applicable, will be determined under Section 10.",
    ],
  },
  {
    id: 5,
    title: "Account Activation and Payment",
    icon: FiCreditCard,
    paragraphs: [
      "An agency's account may be activated after SSDI receives and reviews the required documents, which may include Aadhaar, PAN, GST registration details, business information, authorization documents, and payment for the selected package.",
      "The documents required may vary according to the account type and applicable compliance requirements.",
      "Users and agencies are responsible for providing accurate, complete, current, and authentic information.",
      "If payment is overdue or an agency fails to comply with its selected plan's conditions, the service may remain available for up to 24 hours after the applicable deadline, where supported by the service arrangement. After this period, SSDI may suspend or discontinue access until the issue is resolved.",
      "Payment for a package does not authorize a user to access data beyond the permissions granted to that user.",
    ],
  },
  {
    id: 6,
    title: "Account Reactivation",
    icon: FiRefreshCw,
    paragraphs: [
      "If an account is suspended, deactivated, or closed, reactivation may require approval from the SSDI administrator and completion of any pending verification, documentation, or payment requirements.",
      "Where reactivation requires administrator approval, the account or application will be reactivated no earlier than 24 hours after the required permission has been received, subject to the completion of applicable requirements.",
      "Reactivation is not automatic and may be refused where there are unresolved compliance, security, authorization, or payment issues.",
    ],
  },
  {
    id: 7,
    title: "Bank/NBFC Connection and Authorization",
    icon: FiLink,
    paragraphs: [
      "A bank or NBFC administrative panel will not be connected to the relevant bank's or NBFC's data until the required documentation and written authorization have been received and approved by SSDI.",
      "The concerned bank or NBFC must provide the necessary documents through an authorized communication channel, such as its official email address.",
      "SSDI may request additional documentation or clarification before approving a connection.",
      "Approval to connect a bank or NBFC does not remove the organization's responsibility for the legality, accuracy, and authorized sharing of its data.",
    ],
  },
  {
    id: 8,
    title: "Third Parties and Legal Action",
    icon: FiLock,
    paragraphs: [
      "SSDI will not disclose account information or provide access to confidential account details merely because a third party requests it by telephone, email, or another informal communication channel.",
      "Requests for account information must be verified and handled in accordance with applicable law and SSDI's verification procedures.",
      "Where SSDI receives a valid court order, lawful government or law-enforcement request, legally valid notice, or other enforceable legal requirement, it may provide relevant information or restrict, suspend, or disable an account as permitted or required by law.",
      "SSDI may cooperate with competent authorities in connection with lawful investigations, subject to applicable legal requirements.",
    ],
  },
  {
    id: 9,
    title: "KYC Verification",
    icon: FiCheckCircle,
    paragraphs: [
      "Account activation may require the submission and review of Know Your Customer (KYC) documents, business information, and other verification records.",
      "The agency administrator is responsible for ensuring that the documents and information submitted by the agency or its users are genuine, accurate, current, and lawfully provided.",
      "SSDI reviews submitted documents for account onboarding and compliance purposes but does not guarantee the authenticity of every document solely because it has been submitted or reviewed.",
      "SSDI may request additional verification, reject incomplete or inconsistent documents, or restrict account access where necessary for security, compliance, or authorization reasons.",
    ],
  },
  {
    id: 10,
    title: "Refund Policy",
    icon: FiRotateCcw,
    paragraphs: [
      "A refund request may be submitted within 24 hours of account activation or an applicable paid account or package update.",
      "All refund requests are subject to review and approval by SSDI and the terms applicable to the relevant payment or service.",
      "Where a refund is approved, it will generally be processed through the applicable online payment method within 5–7 working days, subject to payment-provider and banking timelines.",
      "Requests submitted after the 24-hour period may be declined under this policy, except where a refund is required by applicable law or the relevant transaction terms.",
      "Account deletion, cancellation, or suspension does not automatically entitle a user to a refund.",
      "Any mandatory rights or remedies available to a user under applicable law remain unaffected by this policy.",
    ],
  },
  {
    id: 11,
    title: "Account Deletion and Cancellation",
    icon: FiTrash2,
    paragraphs: [
      "Users may request account deletion or cancellation through the available account features or by contacting SSDI.",
      "Account deletion may result in the loss of access to FastRecovery services, case information, search history, or other account-related features.",
      "Before deleting an account, users should ensure that any information they are legally required to retain has been appropriately preserved.",
      "Certain information may be retained where required by law, for security and fraud prevention, to resolve disputes, to maintain required financial or business records, or to establish, exercise, or defend legal claims.",
      "The refund policy in Section 10 continues to apply to account deletion and cancellation.",
    ],
  },
  {
    id: 12,
    title: "Applicable Law and Jurisdiction",
    icon: FiGlobe,
    paragraphs: [
      "These Terms and Conditions shall be governed by the laws of India.",
      "Subject to applicable law, disputes arising from the use of FastRecovery or these Terms and Conditions shall be subject to the jurisdiction of the competent courts in Delhi.",
      "Nothing in this section excludes any mandatory legal rights or jurisdictional requirements that apply under Indian law.",
    ],
  },
  {
    id: 13,
    title: "GST and Taxes",
    icon: FiPercent,
    paragraphs: [
      "Applicable GST and other legally required taxes will be charged in accordance with Indian tax laws.",
      "Where applicable, 18% GST will be charged on the selected package or service charges. The final tax treatment will depend on the nature of the service, the applicable rate, and the relevant tax rules.",
      "The account administrator or purchasing organization is responsible for providing accurate billing and GST details and paying applicable charges.",
      "Tax invoices will be issued as required by applicable law.",
    ],
  },
  {
    id: 14,
    title: "Purpose of the Platform",
    icon: FiTarget,
    paragraphs: [
      "The primary purpose of FastRecovery is to facilitate the authorized transfer of vehicle-related information from participating banks, NBFCs, and authorized legal recovery agencies to their permitted ground-level teams.",
      "When a vehicle is searched for or traced, related search or location information may be recorded through the relevant authorized system, subject to applicable permissions, agreements, and laws.",
      "During an authorized recovery operation, users may upload inventory records and photographs or other supporting evidence, including images, videos, and PDF documents, where those features are available.",
      "Such records may help document the condition of a vehicle and the circumstances of its recovery.",
      "FastRecovery is intended to facilitate information sharing and operational coordination. It must not be used to threaten, harass, unlawfully track, or cause personal harm to any individual.",
    ],
  },
  {
    id: 15,
    title: "Cookies Policy / Notice Acceptance Cookies",
    icon: FiInfo,
    paragraphs: [
      "Type: Persistent Cookies",
      "Administered by: Software Solutions Development India (SSDI)",
      "Purpose: These cookies help identify whether users have accepted the cookie notice displayed on the FastRecovery website. They may be stored on a user's device to remember the user's acceptance and avoid repeatedly displaying the same notice.",
      "The actual cookies used by FastRecovery depend on the website's implementation and configuration. Any additional cookies used for authentication, preferences, analytics, or other purposes should be disclosed where required by applicable law.",
      "Where consent is required for a particular cookie, the website will obtain and manage that consent in accordance with applicable requirements.",
      "Users may be able to manage or delete cookies through their browser settings. Disabling certain cookies may affect the availability or functionality of some website features.",
    ],
  },
  {
    id: 16,
    title: "Children's Privacy",
    icon: FiUsers,
    paragraphs: [
      "FastRecovery is not intended for children under the age of 13. SSDI does not knowingly collect personally identifiable information from children under the age of 13.",
      "If a parent or guardian believes that a child has provided Personal Data to SSDI, they may contact SSDI and request that the matter be reviewed.",
      "If SSDI becomes aware that it has collected Personal Data from a child under the age of 13 without the legally required authorization or consent, it will take appropriate steps to address the issue, including deletion where required by applicable law.",
      "Where parental consent or other authorization is legally required for processing Personal Data, SSDI will follow the applicable legal requirements before processing that information.",
    ],
  },
  {
    id: 17,
    title: "Links to Other Websites",
    icon: FiExternalLink,
    paragraphs: [
      "FastRecovery may contain links to third-party websites, applications, payment services, or other online resources that are not owned or operated by SSDI.",
      "If a user clicks a third-party link, the user may be redirected to that third party's website or service.",
      "SSDI recommends that users review the applicable Privacy Policy, Terms and Conditions, and security practices of third-party websites before sharing personal or financial information with them.",
      "SSDI does not control third-party websites and is not responsible for their content, privacy policies, security measures, or practices, except to the extent responsibility cannot lawfully be excluded.",
      "The inclusion of a third-party link does not necessarily imply endorsement by SSDI.",
    ],
  },
  {
    id: 18,
    title: "Security of Your Personal Data",
    icon: FiLock,
    paragraphs: [
      "SSDI considers the security of Personal Data important and seeks to implement reasonable technical and organizational measures to protect information against unauthorized access, alteration, disclosure, loss, or destruction.",
      "Depending on the service and its configuration, these measures may include access controls, authentication, permission restrictions, and appropriate safeguards for stored or transmitted information.",
      "However, no method of transmission over the Internet or electronic storage is completely secure. SSDI cannot guarantee absolute security.",
      "Users must keep their passwords and login credentials confidential, avoid sharing accounts with unauthorized persons, and promptly report suspected account compromise or unauthorized access.",
      "Where a security incident occurs, SSDI will take appropriate steps in accordance with applicable law.",
    ],
  },
  {
    id: 19,
    title: "Disclosure of Your Personal Data",
    icon: FiDatabase,
    paragraphs: [
      "SSDI may disclose Personal Data only where there is an applicable lawful basis and the disclosure is permitted or required by applicable law.",
    ],
    subsections: [
      {
        title: "19.1 Business Transactions",
        paragraphs: [
          "If SSDI is involved in a merger, acquisition, restructuring, or sale of assets, Personal Data may be transferred as part of the transaction, subject to applicable law and appropriate safeguards.",
          "Where legally required, users will be notified before their Personal Data becomes subject to a different privacy policy or data-handling arrangement.",
        ],
      },
      {
        title: "19.2 Law Enforcement and Public Authorities",
        paragraphs: [
          "Under certain circumstances, SSDI may be required to disclose Personal Data to comply with applicable laws, legal proceedings, court orders, or valid requests from public authorities, including courts and government agencies.",
          "SSDI will review such requests as appropriate and disclose information in accordance with the applicable legal requirements.",
        ],
      },
      {
        title: "19.3 Other Legal Requirements",
        paragraphs: [
          "SSDI may disclose Personal Data where reasonably necessary and legally permitted to:",
        ],
        list: [
          "Comply with a legal obligation.",
          "Protect and defend the rights, property, or legal interests of SSDI.",
          "Prevent, detect, or investigate suspected wrongdoing relating to FastRecovery.",
          "Protect the personal safety of users or the public.",
          "Establish, exercise, or defend legal claims.",
          "Protect against legal liability.",
        ],
        afterList: [
          "Any disclosure will be limited and handled in accordance with applicable law.",
        ],
      },
    ],
  },
  {
    id: 20,
    title: "Access, Correction, and Deletion of Your Personal Data",
    icon: FiEdit3,
    paragraphs: [
      "Users may request access to, correction of, or deletion of Personal Data held by SSDI, subject to applicable law and the relevant service arrangements.",
      "Where FastRecovery provides account settings or other tools for managing personal information, users may use those features to update or delete eligible information.",
      "Users may also contact SSDI to request access to, correction of, or deletion of their Personal Data.",
      "SSDI will review and respond to such requests in accordance with applicable data protection laws and the procedures applicable to the request.",
      "Certain information may need to be retained where required by law or where retention is otherwise lawfully permitted, including for financial and business records, security, fraud prevention, dispute resolution, and the establishment, exercise, or defence of legal claims.",
      "Deleting Personal Data or closing an account may affect access to FastRecovery features and services. Deletion requests do not automatically cancel outstanding payments or create a right to a refund.",
    ],
  },
  {
    id: 21,
    title: "Changes to These Terms and Conditions",
    icon: FiFileText,
    paragraphs: [
      "SSDI may update these Terms and Conditions when necessary to reflect changes in the FastRecovery platform, service arrangements, applicable laws, security requirements, or business operations.",
      "Updated Terms and Conditions will be published on the FastRecovery website with a revised effective or updated date, where appropriate.",
      "Where required by applicable law, SSDI will provide notice of material changes or obtain any necessary consent.",
      "Users are encouraged to review these Terms and Conditions periodically. Continued use of FastRecovery after an update will be subject to the updated terms to the extent permitted by applicable law.",
    ],
  },
  {
    id: 22,
    title: "Contact and Support",
    icon: FiMail,
    paragraphs: [
      "For questions, requests, or concerns relating to these Terms and Conditions, account access, Personal Data, corrections, or deletion requests, users may contact Software Solutions Development India (SSDI) through the official contact details published on the FastRecovery website.",
      "Users should include sufficient information to help SSDI verify the request and respond appropriately. Sensitive information, such as Aadhaar details, passwords, or payment credentials, should not be sent unless specifically required through a secure and appropriate channel.",
    ],
  },
];

const HIGHLIGHTS = [
  { id: 10, label: "24-hour refunds" },
  { id: 12, label: "Delhi jurisdiction" },
  { id: 13, label: "18% GST" },
  { id: 2, label: "Location privacy" },
];

function TermBlock({ paragraphs = [], list, afterList = [] }) {
  return (
    <>
      {paragraphs.map((text) => (
        <p key={text.slice(0, 48)}>{text}</p>
      ))}
      {Array.isArray(list) && list.length > 0 ? (
        <ul className="terms-item__list">
          {list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {afterList.map((text) => (
        <p key={text.slice(0, 48)}>{text}</p>
      ))}
    </>
  );
}

function TermItem({ term, open, onToggle }) {
  const Icon = term.icon;
  const panelId = `term-panel-${term.id}`;
  const triggerId = `term-trigger-${term.id}`;

  return (
    <article id={`term-${term.id}`} className={`terms-item${open ? " is-open" : ""}`}>
      <button
        type="button"
        id={triggerId}
        className="terms-item__trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="terms-item__index">{String(term.id).padStart(2, "0")}</span>
        <span className="terms-item__icon" aria-hidden>
          <Icon size={18} />
        </span>
        <span className="terms-item__title">{term.title}</span>
        <FiChevronDown className="terms-item__chevron" aria-hidden />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        className="terms-item__panel"
        hidden={!open}
      >
        <TermBlock
          paragraphs={term.paragraphs}
          list={term.list}
          afterList={term.afterList}
        />
        {Array.isArray(term.subsections)
          ? term.subsections.map((section) => (
              <div key={section.title} className="terms-item__subsection">
                <h3>{section.title}</h3>
                <TermBlock
                  paragraphs={section.paragraphs}
                  list={section.list}
                  afterList={section.afterList}
                />
              </div>
            ))
          : null}
      </div>
    </article>
  );
}

export default function TermsPage({
  heading = "Terms and Conditions",
  documentTitle = "Terms and Conditions | FastRecovery",
} = {}) {
  const [openIds, setOpenIds] = useState(() => new Set([1]));

  useEffect(() => {
    const previousTitle = document.title;
    document.title = documentTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [documentTitle]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const jumpTo = (id) => {
    setOpenIds((prev) => new Set(prev).add(id));
    requestAnimationFrame(() => {
      document.getElementById(`term-${id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  return (
    <MarketingPageLayout>
      <header className="marketing-page__hero terms-hero">
        <p className="terms-hero__eyebrow">Legal · FastRecovery Website</p>
        <h1 className="marketing-page__title">{heading}</h1>
        <p className="marketing-page__lead">
          Software Solutions Development India (SSDI) — rules for using the FastRecovery App/Web
          platform.
        </p>
        <div className="terms-highlights">
          {HIGHLIGHTS.map((h) => (
            <button
              key={h.id}
              type="button"
              className="terms-highlight"
              onClick={() => jumpTo(h.id)}
            >
              {h.label}
            </button>
          ))}
        </div>
      </header>

      <div className="terms-list">
        {TERMS.map((term) => (
          <TermItem
            key={term.id}
            term={term}
            open={openIds.has(term.id)}
            onToggle={() => toggle(term.id)}
          />
        ))}
      </div>

      <div className="marketing-page__actions">
        <Link to="/" className="primary-page-btn">
          ← Back to Login
        </Link>
      </div>
    </MarketingPageLayout>
  );
}
